/**
 * COUNTRY CODES FOR A CALLER'S NUMBER (NYIA, 2026-10-18).
 *
 * Anthony: "they also need to be able to add a country code but default it
 * to +1 ... let it be changed by selecting and changing". A plain select,
 * so a phone shows its own picker and nothing hangs off the screen.
 *
 * Pure and here because the browser draws the list and the server refuses
 * anything not on it. Texts only go to +1: the shop's line is a US number.
 * Every other code is for calling and email.
 */

export type CountryCode = { code: string; label: string };

export const COUNTRY_CODES: readonly CountryCode[] = [
  { code: "1", label: "+1 US / Canada" },
  { code: "52", label: "+52 Mexico" },
  { code: "44", label: "+44 United Kingdom" },
  { code: "353", label: "+353 Ireland" },
  { code: "61", label: "+61 Australia" },
  { code: "64", label: "+64 New Zealand" },
  { code: "33", label: "+33 France" },
  { code: "49", label: "+49 Germany" },
  { code: "39", label: "+39 Italy" },
  { code: "34", label: "+34 Spain" },
  { code: "351", label: "+351 Portugal" },
  { code: "31", label: "+31 Netherlands" },
  { code: "32", label: "+32 Belgium" },
  { code: "41", label: "+41 Switzerland" },
  { code: "46", label: "+46 Sweden" },
  { code: "47", label: "+47 Norway" },
  { code: "45", label: "+45 Denmark" },
  { code: "48", label: "+48 Poland" },
  { code: "380", label: "+380 Ukraine" },
  { code: "7", label: "+7 Russia / Kazakhstan" },
  { code: "90", label: "+90 Turkey" },
  { code: "30", label: "+30 Greece" },
  { code: "972", label: "+972 Israel" },
  { code: "971", label: "+971 UAE" },
  { code: "966", label: "+966 Saudi Arabia" },
  { code: "20", label: "+20 Egypt" },
  { code: "27", label: "+27 South Africa" },
  { code: "234", label: "+234 Nigeria" },
  { code: "233", label: "+233 Ghana" },
  { code: "254", label: "+254 Kenya" },
  { code: "91", label: "+91 India" },
  { code: "92", label: "+92 Pakistan" },
  { code: "880", label: "+880 Bangladesh" },
  { code: "86", label: "+86 China" },
  { code: "852", label: "+852 Hong Kong" },
  { code: "886", label: "+886 Taiwan" },
  { code: "81", label: "+81 Japan" },
  { code: "82", label: "+82 South Korea" },
  { code: "63", label: "+63 Philippines" },
  { code: "84", label: "+84 Vietnam" },
  { code: "66", label: "+66 Thailand" },
  { code: "62", label: "+62 Indonesia" },
  { code: "60", label: "+60 Malaysia" },
  { code: "65", label: "+65 Singapore" },
  { code: "55", label: "+55 Brazil" },
  { code: "54", label: "+54 Argentina" },
  { code: "57", label: "+57 Colombia" },
  { code: "56", label: "+56 Chile" },
  { code: "51", label: "+51 Peru" },
  { code: "58", label: "+58 Venezuela" },
  { code: "593", label: "+593 Ecuador" },
  { code: "502", label: "+502 Guatemala" },
  { code: "503", label: "+503 El Salvador" },
  { code: "504", label: "+504 Honduras" },
  { code: "506", label: "+506 Costa Rica" },
  { code: "507", label: "+507 Panama" },
  { code: "53", label: "+53 Cuba" },
  { code: "1876", label: "+1 876 Jamaica" },
  { code: "1809", label: "+1 809 Dominican Republic" },
  { code: "509", label: "+509 Haiti" },
];

export const isCountryCode = (c: string): boolean => COUNTRY_CODES.some((x) => x.code === c);

/**
 * Digits a number needs after its code: +1 is exactly ten; anything else
 * is 4 to 14, the most E.164 leaves after a code. "+1 876" and "+1 809"
 * carry their area code in the code, so seven digits follow.
 */
export function phoneProblem(code: string, digits: string): string | null {
  if (!isCountryCode(code)) return "Pick a country code from the list.";
  if (!/^\d+$/.test(digits)) return "Type the number in digits.";
  if (code === "1") return digits.length === 10 ? null : "A +1 number is ten digits, area code first.";
  if (code.length === 4 && code.startsWith("1")) return digits.length === 7 ? null : "That number is seven digits after the code.";
  return digits.length >= 4 && digits.length + code.length <= 15 ? null : "That number is the wrong length for its country.";
}

/** "+1 (917) 555-0100", "+44 7700900123": how a saved number reads. */
export function showNumber(code: string, digits: string): string {
  if (code === "1" && digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `+${code} ${digits}`;
}

/**
 * THE COUNTRY A CALLER CAME IN FROM (Neelum at NYIA, 2026-10-29: "country
 * code isn't appearing in shopdesk ... a caller from Italy").
 *
 * The call log keeps the last ten digits and files them as +1, so Tina
 * calling from +39 350 851 2278 read "(350) 851-2278". CallRail's own
 * number on the enquiry still has the "+39". This reads that: a number
 * written with a plus and a code from the list other than +1 gives the
 * code and what follows it, longest code first ("+1876" before "+1").
 * Anything else, null: the caller is +1, as before.
 */
export function foreignOf(raw: string | null | undefined): { cc: string; phone: string } | null {
  const s = String(raw ?? "").trim();
  if (!s.startsWith("+")) return null;
  const d = s.replace(/\D/g, "");
  const code = COUNTRY_CODES.map((x) => x.code).filter((c) => d.startsWith(c)).sort((a, b) => b.length - a.length)[0];
  if (!code || code === "1") return null;
  const phone = d.slice(code.length);
  return phoneProblem(code, phone) ? null : { cc: code, phone };
}
